import { action, computed, makeAutoObservable } from 'mobx';
import { cloneDeep } from 'lodash';
import { PromotedEventsState } from 'src/AppState/CmsState/PromotedEventsState/PromotedEventsState';
import { arrayMove } from 'react-movable';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getAllPromotedSports';
import { TrpcClient } from 'src/AppState/TrpcClient';

type PromotedSportDataModelType = Response200Type[number];

export class PromotedSportsListState {
    public constructor(
        private readonly trpcClient: TrpcClient,
        private promotedEventsSectionState: PromotedEventsState
    ) {
        this.promotedEventsSectionState.setSportsDataFromResource();
        makeAutoObservable(this);
    }

    @computed public get sportsArrayForDraggableList(): Array<PromotedSportDataModelType> {
        return this.promotedEventsSectionState.sportsArray;
    }

    filterElements = (sport: PromotedSportDataModelType, oldIndex: number, newIndex: number): boolean => {
        if (oldIndex > newIndex) {
            return sport.display_order >= newIndex && sport.display_order < oldIndex;
        } else {
            return sport.display_order <= newIndex && sport.display_order > oldIndex;
        }
    };

    updateDisplayOrder = (
        arrayMoved: Array<PromotedSportDataModelType>,
        oldIndex: number,
        newIndex: number
    ): Array<PromotedSportDataModelType> => {
        const tempMovedArray = cloneDeep(arrayMoved);
        const promotedSportsToMove = arrayMoved
            .filter((sport) => this.filterElements(sport, oldIndex, newIndex))
            .map((c) => c.id);
        const shouldIncrementIndexes = oldIndex > newIndex;
        const draggedSport = arrayMoved.find((sport) => sport.display_order === oldIndex);

        for (let i = 0; i < tempMovedArray.length; i++) {
            const sport = tempMovedArray[i];
            if (sport === undefined) {
                return tempMovedArray;
            }
            if (promotedSportsToMove.includes(sport.id)) {
                if (shouldIncrementIndexes) {
                    sport.display_order = sport.display_order + 1;
                } else {
                    sport.display_order = sport.display_order - 1;
                }
            }

            if (sport.id === draggedSport?.id) {
                sport.display_order = newIndex;
            }
        }
        return tempMovedArray;
    };

    @action handleSportsOrder = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
    }): Promise<void> => {
        const displayOrderOfOldIndexElement = this.promotedEventsSectionState.sportsArray[oldIndex]?.display_order;
        const displayOrderOfNewIndexElement = this.promotedEventsSectionState.sportsArray[newIndex]?.display_order;
        const draggedSport = this.promotedEventsSectionState.sportsArray[oldIndex];

        if (displayOrderOfNewIndexElement === undefined || displayOrderOfOldIndexElement === undefined) {
            return;
        }

        const arrayMoved = arrayMove(this.promotedEventsSectionState.sportsArray, oldIndex, newIndex);
        const arrayWithUpdatedDisplayOrder = this.updateDisplayOrder(
            arrayMoved,
            displayOrderOfOldIndexElement,
            displayOrderOfNewIndexElement
        );
        this.promotedEventsSectionState.setSportsData(arrayWithUpdatedDisplayOrder);

        if (draggedSport === undefined) {
            return;
        }

        await this.trpcClient.client.promotedSports.changePromotedSportsOrder.mutate({
            data: {
                id: draggedSport.id,
                new_order: displayOrderOfNewIndexElement,
                old_order: displayOrderOfOldIndexElement,
            },
        });

        this.promotedEventsSectionState.promotedEventsResource.refresh();
    };
}
